import { cva, type VariantProps } from 'class-variance-authority'
import { type ComponentPropsWithoutRef, forwardRef } from 'react'

const typographyVariants = cva('h-fit w-fit', {
	variants: {
		type: {
			serif: 'font-serif',
			sans: 'font-sans',
			mono: 'font-mono',
		},
		size: {
			display:
				'text-[clamp(2.5rem,calc(5vw+2.25rem),9rem)] leading-[clamp(60px,calc(4vw+2rem),8rem)]',
			pageTitle:
				'text-[clamp(2rem,calc(4vw+1.75rem),7rem)] leading-[clamp(50px,calc(4vw+2rem),7rem)]',
			huge: 'text-[clamp(40px,calc(3vw+1.75rem),6rem)] leading-[clamp(40px,calc(3vw+1.75rem),6rem)]',
			h1: 'text-[clamp(35px,calc(3vw+1.5rem),5rem)] leading-[clamp(35px,calc(3vw+1.5rem),5rem)]',
			h2: 'text-[clamp(30px,calc(2.5vw+1.25rem),4rem)] leading-[clamp(35px,calc(2.5vw+1.5rem),4rem)]',
			h3: 'text-[clamp(25px,calc(2vw+1rem),3rem)] leading-[clamp(25px,calc(2vw+1rem),3rem)]',
			h4: 'text-[clamp(22px,calc(1.5vw+0.875rem),2.5rem)] leading-[clamp(22px,calc(1.5vw+0.875rem),2.5rem)]',
			h5: 'text-[clamp(20px,calc(1.25vw+0.75rem),2rem)] leading-[clamp(20px,calc(1.25vw+0.75rem),2rem)]',
			h6: 'text-[clamp(18px,calc(1vw+0.625rem),1.5rem)] leading-[clamp(18px,calc(1vw+0.625rem),1.5rem)]',
			large:
				'text-[clamp(16px,calc(1vw+0.5rem),1.25rem)] leading-[clamp(16px,calc(1vw+0.5rem),1.5rem)]',
			p: 'text-body-2xs font-light sm:text-body-xs 2xl:text-body-sm',
			small: 'text-body-2xs font-light sm:text-body-xs',
		},
		weight: {
			thin: '!font-thin',
			extralight: '!font-extralight',
			light: '!font-light',
			normal: '!font-normal',
			medium: '!font-medium',
			bold: '!font-bold',
			extrabold: '!font-extrabold',
			black: '!font-black',
		},
	},
	defaultVariants: {
		type: 'sans',
		weight: null,
		size: null,
	},
})

export interface TypographyProps
	extends Omit<ComponentPropsWithoutRef<'p'>, 'type'>,
		VariantProps<typeof typographyVariants> {
	as?: 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

const Text = forwardRef(
	({ className, as, type, size, weight, ...props }: TypographyProps, ref) => {
		const Comp = as || 'p'

		return (
			<Comp
				// @ts-ignore
				ref={ref}
				className={typographyVariants({
					type,
					size,
					weight,
					className,
				})}
				{...props}
			>
				{props.children}
			</Comp>
		)
	},
)

export { Text }
